import { computed, shallowRef } from 'vue'
import type { ComputedRef } from 'vue'

import type { MapViewport } from '@/components/map/mapMode'
import type { TransitIndex } from '@/composables/useTransitIndex'
import type { IndexedStop } from '@/composables/useTransitIndex'

/**
 * Below this zoom the stop layer is hidden entirely. At city-wide zoom, 4,991
 * dots render as an unreadable grey smear anyway, and the reference app behaves
 * the same way.
 */
export const STOP_ZOOM_FLOOR = 14

/**
 * Hard ceiling on rendered stops. Dense central Hanoi can put several hundred
 * stops in one viewport at zoom 14.
 */
export const MAX_VISIBLE_STOPS = 400

export interface VisibleStopsResult {
  stops: ComputedRef<IndexedStop[]>
  /** True when the zoom floor is hiding the layer, so the UI can say so. */
  belowZoomFloor: ComputedRef<boolean>
  /** True when the cap dropped stops that are actually in view. */
  capped: ComputedRef<boolean>
  onViewportChange: (viewport: MapViewport) => void
}

/**
 * Which stops the home map should draw.
 *
 * Three guards, all needed: the zoom floor, viewport culling through the grid
 * index, and the count cap. Without them the map mounts the whole network.
 */
export function useVisibleStops(index: ComputedRef<TransitIndex | undefined>): VisibleStopsResult {
  const viewport = shallowRef<MapViewport>()

  const inView = computed<IndexedStop[]>(() => {
    const current = viewport.value
    const transit = index.value
    if (!current || !transit) return []
    if (current.zoom < STOP_ZOOM_FLOOR) return []
    return transit.spatial.queryBounds(current.bounds) as IndexedStop[]
  })

  const belowZoomFloor = computed(
    () => viewport.value !== undefined && viewport.value.zoom < STOP_ZOOM_FLOOR,
  )

  const capped = computed(() => inView.value.length > MAX_VISIBLE_STOPS)

  const stops = computed(() =>
    capped.value ? inView.value.slice(0, MAX_VISIBLE_STOPS) : inView.value,
  )

  return {
    stops,
    belowZoomFloor,
    capped,
    onViewportChange(next) {
      viewport.value = next
    },
  }
}
